import { Injectable, inject } from '@angular/core';
import { AbookSnackbarService } from '@app/shared/components/abook-snackbar/abook-snackbar.service';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { Role } from '@app/shared/models/enums';
import { AuthenticationService } from './authentication.service';
import { isAssociationLogin, isOrganisationType } from '@shared/utilities/association-role.util';

/**
 * The administrator roles the admin app is for, besides an organisation's people
 * ({@link isAssociationLogin}). Everything else is signed off and told why.
 */
const ADMIN_ROLES: readonly number[] = [
  Role.SuperAdmin,
  Role.AdminArea,
  Role.Admin,
  Role.AdminReadOnly,
  Role.AdminReadOnlyAllLocations,
];

/**
 * Why an organisation's own customer row (forening, virksomhed, intern) is turned away: it is the
 * organisation's shared login, and its people sign in with their own users. Echoes abook-auth's
 * message for a closed shared login, but says "not here" rather than "closed": on a tenant that has
 * not closed it (`customertype.SignInDisabled` 0) the row still signs in to the customer app.
 */
export const ORGANISATION_LOGIN_REFUSED =
  'Organisationens fælles login giver ikke adgang her. Log ind med din egen bruger, eller bed en ansvarlig i organisationen eller administrationen om at tilknytte dig.';

/**
 * Guards the signed-in area, and keeps the two refusals apart.
 *
 * No session at all is a login problem: the visitor is handed to the identity provider. A session
 * whose role has no business in the admin app is a permission problem, and only that one belongs
 * on `/authentication/unauthorized`. Sending the sessionless there as well meant an ordinary
 * login went through a page that claims a permission was missing — and that page answered by
 * processing the login callback again.
 *
 * The session is settled before the router's first navigation (an app initializer awaits
 * `checkUser()`), so the first `userData$` value is the real answer.
 */
@Injectable({ providedIn: 'root' })
export class AuthorizationGuard {
  private oidcSecurityService = inject(OidcSecurityService);
  private authenticationService = inject(AuthenticationService);
  private router = inject(Router);
  private abookSnackbarService = inject(AbookSnackbarService);

  canActivate(_route?: ActivatedRouteSnapshot, state?: RouterStateSnapshot): Observable<boolean> {
    return this.oidcSecurityService.userData$.pipe(
      // One question, one answer: signing a refused user off empties `userData$`, and a guard
      // still listening would read that emission as "no session" and start a login.
      take(1),
      map(({ userData }) => {
        if (userData == null) {
          // The identity provider returns to the app root; signin sends the visitor on from there.
          this.authenticationService.signIn(state?.url);

          return false;
        }

        // TODO - check capabilities
        const primaryRole = Number(userData.primary_role);
        if (ADMIN_ROLES.includes(primaryRole) || isAssociationLogin(primaryRole)) {
          return true;
        }

        // `logoff()` redirects the browser to the identity provider's end-session endpoint, so
        // the snackbar and the route below are a fallback for the case where that redirect does
        // not happen — normally the user is returned to `postLogoutRedirectUri` and ends up at
        // the login page instead of seeing either.
        this.abookSnackbarService.showError(
          'Adgang nægtet',
          isOrganisationType(primaryRole) ? ORGANISATION_LOGIN_REFUSED : 'Kun administrator har adgang',
        );
        this.oidcSecurityService.logoff().subscribe();
        this.router.navigate(['authentication/unauthorized']);

        return false;
      }),
    );
  }
}
